/**
 * Document renderer for Word, PowerPoint, Excel files
 * Uses Microsoft Office Online viewer for public URLs, download prompt for private/signed URLs
 */

import { useState } from 'react';
import { Stack, Text, Badge } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { Button, Card } from '@/components/ui';
import { getFileIcon, getFileMimeColor, getFileTypeName } from '../../../utils';
import { formatFileSize } from '../../../../utils/formatting';
import type { ViewerFile } from '../../../types';

interface DocumentRendererProps {
  file: ViewerFile;
  onDownload?: () => void;
}

/**
 * Check if a URL is likely a signed/private URL (S3, Azure, GCS)
 */
function isSignedUrl(url: string): boolean {
  return (
    url.includes('X-Amz-Signature') ||
    url.includes('X-Amz-Credential') ||
    url.includes('sig=') ||
    url.includes('sv=') ||
    url.includes('Signature=')
  );
}

export function DocumentRenderer({ file, onDownload }: DocumentRendererProps) {
  const FileIcon = getFileIcon(file.mimeType);
  const iconColor = getFileMimeColor(file.mimeType);
  const typeName = getFileTypeName(file.mimeType);
  const [viewerFailed, setViewerFailed] = useState(false);

  // Try online viewers for public (non-signed) URLs
  if (file.url && !isSignedUrl(file.url) && !viewerFailed) {
    // Use Microsoft Office Online viewer (better Office doc support)
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
    // Fallback to Google Docs Viewer
    const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(file.url)}&embedded=true`;

    const isOfficeDoc =
      file.mimeType.includes('word') ||
      file.mimeType.includes('document') ||
      file.mimeType.includes('excel') ||
      file.mimeType.includes('spreadsheet') ||
      file.mimeType.includes('powerpoint') ||
      file.mimeType.includes('presentation') ||
      /\.(docx?|xlsx?|pptx?)$/i.test(file.fileName);

    const viewerUrl = isOfficeDoc ? officeViewerUrl : googleViewerUrl;

    return (
      <div style={{ width: '100%', height: '100%', padding: 8 }}>
        <iframe
          src={viewerUrl}
          title={file.fileName}
          onError={() => setViewerFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: 8,
            backgroundColor: 'white',
          }}
        />
      </div>
    );
  }

  // Download prompt for private/signed URLs, no URL, or when viewer failed
  return (
    <Stack align="center" justify="center" h="100%" gap="lg">
      <Card variant="elevated" p="xl" style={{ minWidth: 320, maxWidth: 420 }}>
        <Stack align="center" gap="md">
          <FileIcon size={48} color={iconColor} />
          <Text size="lg" fw={500} c="white" ta="center" lineClamp={2}>
            {file.fileName}
          </Text>
          <Badge variant="light" color="gray" size="lg">
            {typeName}
          </Badge>
          {file.fileSize > 0 && (
            <Text size="sm" c="dimmed">
              {formatFileSize(file.fileSize)}
            </Text>
          )}
          <Text size="xs" c="dimmed" ta="center">
            This document type requires downloading to view
          </Text>
          {onDownload && (
            <Button
              variant="gradient"
              gradientVariant="primary"
              leftSection={<IconDownload size={16} />}
              onClick={onDownload}
            >
              Download to View
            </Button>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
